// src/pages/QbusAutomations/AutomationEditor.jsx
// Full-screen ReactFlow editor with debug overlay, export/import, execution picker, debug panel
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
  Popover,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Slider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  DataObject as VariablesIcon,
  Add as AddIcon,
  BugReport as DebugIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as RunningIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Replay as ReplayIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as StepForwardIcon,
  SkipPrevious as StepBackIcon,
  ViewSidebar as PanelIcon
} from '@mui/icons-material';
import BlockPalette, { BLOCK_DEFINITIONS } from './BlockPalette';
import TriggerNode from './nodes/TriggerNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import FlowControlNode from './nodes/FlowControlNode';
import NodeConfigPanel from './NodeConfigPanel';
import NodeDataDrawer from './NodeDataDrawer';
import DebugPanel from './DebugPanel';
import { qbusApi } from '../../services/apiClient';

// Build lookup: blockType → category
const blockTypeToCategory = {};
BLOCK_DEFINITIONS.forEach((group) => {
  group.blocks.forEach((block) => {
    blockTypeToCategory[block.blockType] = group.category;
  });
});

const nodeTypes = {
  trigger: TriggerNode,
  condition: ConditionNode,
  action: ActionNode,
  flow_control: FlowControlNode,
};

const categoryColors = {
  trigger: '#ef6c00',
  condition: '#1976d2',
  action: '#388e3c',
  flow_control: '#7b1fa2',
};

let nodeIdCounter = 0;
const getNodeId = () => `node_${Date.now()}_${++nodeIdCounter}`;

const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  style: { strokeWidth: 2 },
};

const AutomationEditor = ({ automation, locationId, onSave, onClose }) => {
  const reactFlowWrapper = useRef(null);
  const importInputRef = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Form state
  const [name, setName] = useState(automation?.name || '');
  const [description, setDescription] = useState(automation?.description || '');
  const [saving, setSaving] = useState(false);

  // Flow-level variables (persisted in flow_definition.variables)
  const [flowVariables, setFlowVariables] = useState(
    automation?.flow_definition?.variables || []
  );
  const [showVariables, setShowVariables] = useState(false);

  // Selected node
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Debug mode
  const [debugMode, setDebugMode] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugPopover, setDebugPopover] = useState({ anchorEl: null, data: null });

  // Playback state
  const [playbackStep, setPlaybackStep] = useState(-1); // -1 = show all, 0+ = step index
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackTimer = useRef(null);

  // Initialize nodes/edges from existing flow or empty
  const initialFlow = automation?.flow_definition || { nodes: [], edges: [] };
  const [nodes, setNodes, onNodesChange] = useNodesState(initialFlow.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialFlow.edges || []);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  // Selected execution object
  const selectedExecution = useMemo(
    () => executions.find(e => e.id === selectedExecutionId) || null,
    [executions, selectedExecutionId]
  );

  // Load executions when entering debug mode or when editor opens
  const loadExecutions = useCallback(async () => {
    if (!automation?.id) return;
    try {
      const res = await qbusApi.getAutomationExecutions(automation.id, { limit: 10 });
      const execs = res.data.executions || [];
      setExecutions(execs);
      if (execs.length > 0 && !selectedExecutionId) {
        setSelectedExecutionId(execs[0].id);
      }
    } catch { /* ignore */ }
  }, [automation?.id, selectedExecutionId]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  // Build nodeId → execution step map from selected execution
  const executionMap = useMemo(() => {
    if (!selectedExecution?.execution_log) return {};
    const map = {};
    for (const step of selectedExecution.execution_log) {
      map[step.nodeId] = step;
    }
    return map;
  }, [selectedExecution]);

  const executedNodeIds = useMemo(() => new Set(Object.keys(executionMap)), [executionMap]);

  // Playback: set of nodeIds that should be highlighted at current step
  const playbackNodeIds = useMemo(() => {
    if (playbackStep < 0 || !selectedExecution?.execution_log) return null;
    const steps = selectedExecution.execution_log;
    const ids = new Set();
    for (let i = 0; i <= Math.min(playbackStep, steps.length - 1); i++) {
      ids.add(steps[i].nodeId);
    }
    return ids;
  }, [playbackStep, selectedExecution]);

  // Playback animation
  useEffect(() => {
    if (!isPlaying || !selectedExecution?.execution_log) return;
    const totalSteps = selectedExecution.execution_log.length;
    const interval = 1000 / playbackSpeed;

    playbackTimer.current = setInterval(() => {
      setPlaybackStep(prev => {
        if (prev >= totalSteps - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, interval);

    return () => clearInterval(playbackTimer.current);
  }, [isPlaying, playbackSpeed, selectedExecution]);

  // Inject _debugStep into node data when debug mode is active
  const displayNodes = useMemo(() => {
    if (!debugMode || !selectedExecution) return nodes;
    return nodes.map(node => {
      const step = executionMap[node.id];
      if (!step) {
        // If playback is active, grey out un-reached nodes
        if (playbackNodeIds && !playbackNodeIds.has(node.id)) {
          return { ...node, data: { ...node.data, _debugStep: undefined }, style: { ...node.style, opacity: 0.3 } };
        }
        return { ...node, data: { ...node.data, _debugStep: undefined } };
      }
      // If playback is active and this node hasn't been reached yet
      if (playbackNodeIds && !playbackNodeIds.has(node.id)) {
        return { ...node, data: { ...node.data, _debugStep: undefined }, style: { ...node.style, opacity: 0.3 } };
      }
      return { ...node, data: { ...node.data, _debugStep: step }, style: { ...node.style, opacity: 1 } };
    });
  }, [nodes, debugMode, selectedExecution, executionMap, playbackNodeIds]);

  // Connection validation
  const isValidConnection = useCallback(
    (connection) => {
      if (connection.source === connection.target) return false;
      const exists = edges.some(
        (e) =>
          e.source === connection.source &&
          e.sourceHandle === connection.sourceHandle &&
          e.target === connection.target
      );
      if (exists) return false;

      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (!sourceNode || !targetNode) return false;

      if (targetNode.type === 'trigger') return false;

      // Parallel (fork) nodes: allow one edge per branch handle (like conditions)
      if (sourceNode.type === 'flow_control' && sourceNode.data?.blockType === 'parallel') {
        const existingFromHandle = edges.some(
          (e) => e.source === connection.source && e.sourceHandle === connection.sourceHandle
        );
        if (existingFromHandle) return false;
        return true;
      }

      // Join nodes as target: allow multiple incoming edges (no limit)
      // (the duplicate check above already prevents exact same connection)

      if (sourceNode.type !== 'condition' && sourceNode.type !== 'flow_control') {
        const existingFromHandle = edges.some(
          (e) => e.source === connection.source && e.sourceHandle === connection.sourceHandle
        );
        if (existingFromHandle) return false;
      }

      if (sourceNode.type === 'condition') {
        const existingFromHandle = edges.some(
          (e) => e.source === connection.source && e.sourceHandle === connection.sourceHandle
        );
        if (existingFromHandle) return false;
      }

      return true;
    },
    [nodes, edges]
  );

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const color = sourceNode ? categoryColors[sourceNode.type] || '#666' : '#666';

      let edgeColor = color;
      let edgeLabel;
      if (sourceNode?.type === 'condition') {
        edgeColor = params.sourceHandle === 'true' ? '#2e7d32' : '#c62828';
        edgeLabel = params.sourceHandle?.toUpperCase();
      } else if (sourceNode?.type === 'flow_control' && sourceNode.data?.blockType === 'parallel') {
        // Label parallel branch edges with branch number
        const branchIdx = params.sourceHandle?.replace('branch-', '');
        edgeLabel = branchIdx !== undefined ? `Branch ${Number(branchIdx) + 1}` : undefined;
      }

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
            style: { stroke: edgeColor, strokeWidth: 2 },
            label: edgeLabel,
            labelStyle: { fontWeight: 700, fontSize: 10 },
          },
          eds
        )
      );
    },
    [nodes, setEdges]
  );

  // Apply debug overlay to edges
  const displayEdges = useMemo(() => {
    if (!debugMode || !selectedExecution) return edges;
    const activeNodeIds = playbackNodeIds || executedNodeIds;

    return edges.map(edge => {
      const sourceExecuted = activeNodeIds.has(edge.source);
      const targetExecuted = activeNodeIds.has(edge.target);
      const sourceStep = executionMap[edge.source];
      const bothExecuted = sourceExecuted && targetExecuted;

      if (bothExecuted && sourceStep?.status === 'completed') {
        return {
          ...edge,
          animated: true,
          style: { ...edge.style, stroke: '#2e7d32', strokeWidth: 3 },
        };
      } else if (sourceExecuted && sourceStep?.status === 'failed') {
        return {
          ...edge,
          style: { ...edge.style, stroke: '#c62828', strokeWidth: 3, strokeDasharray: '5,5' },
        };
      } else if (!sourceExecuted && !targetExecuted) {
        return {
          ...edge,
          style: { ...edge.style, stroke: '#bdbdbd', strokeWidth: 1, opacity: 0.5 },
        };
      }
      return edge;
    });
  }, [edges, debugMode, selectedExecution, executionMap, executedNodeIds, playbackNodeIds]);

  // Edge click — show debug popover
  const onEdgeClick = useCallback((_event, edge) => {
    if (!debugMode || !selectedExecution) return;
    const sourceStep = executionMap[edge.source];
    const targetStep = executionMap[edge.target];
    if (!sourceStep) return;
    setDebugPopover({
      anchorEl: _event.target,
      data: { edge, sourceStep, targetStep },
    });
  }, [debugMode, selectedExecution, executionMap]);

  // Drag-and-drop from palette
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      const blockType = event.dataTransfer.getData('application/reactflow-blockType');
      const label = event.dataTransfer.getData('application/reactflow-label');
      const category = event.dataTransfer.getData('application/reactflow-category');

      if (!blockType || !category) return;

      if (category === 'trigger') {
        const existingTrigger = nodes.find((n) => n.type === 'trigger');
        if (existingTrigger) return;
      }

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const newNode = {
        id: getNodeId(),
        type: category,
        position,
        data: {
          label,
          blockType,
          config: {},
          summary: '',
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, nodes, setNodes]
  );

  // Node selection
  const onNodeClick = useCallback((_event, node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setDebugPopover({ anchorEl: null, data: null });
  }, []);

  // Update node data from config panel
  const handleNodeConfigChange = useCallback(
    (nodeId, newData) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n))
      );
    },
    [setNodes]
  );

  // Delete selected node
  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) =>
      eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId)
    );
    setSelectedNodeId(null);
  }, [selectedNodeId, setNodes, setEdges]);

  // Save
  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const viewport = reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 };
      await onSave({
        name: name.trim(),
        description: description.trim(),
        flow_definition: { nodes, edges, viewport, variables: flowVariables },
      });
    } finally {
      setSaving(false);
    }
  };

  // Export automation as JSON
  const handleExport = () => {
    const exportData = {
      name,
      description,
      flow_definition: {
        nodes,
        edges,
        variables: flowVariables,
        viewport: reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 },
      },
      _exportedAt: new Date().toISOString(),
      _version: 1,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `automation-${name.replace(/[^a-zA-Z0-9]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import automation from JSON file
  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.flow_definition?.nodes || !data.flow_definition?.edges) {
          alert('Invalid automation file: missing flow_definition.nodes or edges');
          return;
        }
        setName(data.name || name);
        setDescription(data.description || description);
        setNodes(data.flow_definition.nodes);
        setEdges(data.flow_definition.edges);
        setFlowVariables(data.flow_definition.variables || []);
        if (reactFlowInstance && data.flow_definition.viewport) {
          reactFlowInstance.setViewport(data.flow_definition.viewport);
        }
      } catch (err) {
        alert('Failed to parse automation file: ' + err.message);
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be imported again
    event.target.value = '';
  };

  // Replay execution
  const handleReplay = async () => {
    if (!automation?.id || !selectedExecutionId) return;
    try {
      const res = await qbusApi.replayExecution(automation.id, selectedExecutionId);
      const newExec = res.data.execution;
      // Add to front of executions list and select it
      setExecutions(prev => [newExec, ...prev.slice(0, 9)]);
      setSelectedExecutionId(newExec.id);
    } catch (err) {
      console.error('Replay failed:', err);
    }
  };

  // Playback controls
  const startPlayback = () => {
    setPlaybackStep(0);
    setIsPlaying(true);
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    clearInterval(playbackTimer.current);
  };

  const resetPlayback = () => {
    stopPlayback();
    setPlaybackStep(-1);
  };

  const totalSteps = selectedExecution?.execution_log?.length || 0;

  // Stats
  const triggerCount = nodes.filter((n) => n.type === 'trigger').length;
  const conditionCount = nodes.filter((n) => n.type === 'condition').length;
  const actionCount = nodes.filter((n) => n.type === 'action').length;

  const extractedVarCount = nodes.reduce((count, n) => {
    const bt = n.data?.blockType;
    if (bt === 'extract_params' || bt === 'regex_extract') {
      const extractions = n.data?.config?.extractions || [];
      return count + extractions.filter(e => e.variableName || e.outputVariable).length;
    }
    return count;
  }, 0);
  const totalVarCount = flowVariables.filter(v => v.name).length + extractedVarCount;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <Paper
        elevation={2}
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: 0,
          zIndex: 10,
          flexWrap: 'wrap',
        }}
      >
        <IconButton onClick={onClose} size="small">
          <BackIcon />
        </IconButton>

        <TextField
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ width: 220 }}
          required
        />
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ flex: 1, minWidth: 150 }}
        />

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip label={`${triggerCount}T`} size="small" sx={{ bgcolor: '#ef6c00', color: 'white', height: 22 }} />
          <Chip label={`${conditionCount}C`} size="small" sx={{ bgcolor: '#1976d2', color: 'white', height: 22 }} />
          <Chip label={`${actionCount}A`} size="small" sx={{ bgcolor: '#388e3c', color: 'white', height: 22 }} />
          <Chip
            icon={<VariablesIcon sx={{ fontSize: 14 }} />}
            label={`${totalVarCount}`}
            size="small"
            variant={showVariables ? 'filled' : 'outlined'}
            color="secondary"
            onClick={() => setShowVariables(!showVariables)}
            sx={{ cursor: 'pointer', height: 22 }}
          />
        </Box>

        {/* Debug toggle */}
        {executions.length > 0 && (
          <Chip
            icon={<DebugIcon sx={{ fontSize: 14 }} />}
            label={debugMode ? 'Debug ON' : 'Debug'}
            size="small"
            variant={debugMode ? 'filled' : 'outlined'}
            color={debugMode ? 'warning' : 'default'}
            onClick={() => {
              setDebugMode(!debugMode);
              if (!debugMode) loadExecutions();
            }}
            sx={{ cursor: 'pointer', height: 22 }}
          />
        )}

        {/* Export / Import */}
        <Tooltip title="Export as JSON">
          <IconButton size="small" onClick={handleExport}>
            <ExportIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Import from JSON">
          <IconButton size="small" onClick={() => importInputRef.current?.click()}>
            <ImportIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={importInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleImport}
        />

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !name.trim()}
          size="small"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Paper>

      {/* Debug info bar */}
      {debugMode && (
        <Box sx={{ px: 2, py: 0.5, bgcolor: '#fff3e0', borderBottom: '1px solid #ffe0b2', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <DebugIcon sx={{ fontSize: 14, color: '#e65100' }} />

          {/* Execution picker */}
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <Select
              value={selectedExecutionId || ''}
              onChange={(e) => {
                setSelectedExecutionId(e.target.value);
                resetPlayback();
              }}
              sx={{ fontSize: '0.7rem', height: 28 }}
              displayEmpty
            >
              {executions.map(exec => (
                <MenuItem key={exec.id} value={exec.id} sx={{ fontSize: '0.7rem' }}>
                  {new Date(exec.started_at).toLocaleString()}
                  {' — '}
                  <Chip
                    label={exec.status}
                    size="small"
                    color={exec.status === 'completed' ? 'success' : exec.status === 'failed' ? 'error' : 'warning'}
                    sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }}
                  />
                  {exec.is_test && (
                    <Chip label="Test" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }} />
                  )}
                  {' — '}
                  {(exec.execution_log || []).length} steps
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Replay button */}
          {selectedExecutionId && (
            <Tooltip title="Replay with same trigger data">
              <IconButton size="small" onClick={handleReplay} sx={{ color: '#e65100' }}>
                <ReplayIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Playback controls */}
          {totalSteps > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Step back">
                <IconButton size="small" onClick={() => setPlaybackStep(Math.max(0, playbackStep - 1))} disabled={playbackStep <= 0}>
                  <StepBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {!isPlaying ? (
                <Tooltip title="Play execution">
                  <IconButton size="small" onClick={startPlayback}>
                    <PlayIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Pause">
                  <IconButton size="small" onClick={stopPlayback}>
                    <PauseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Step forward">
                <IconButton size="small" onClick={() => setPlaybackStep(Math.min(totalSteps - 1, playbackStep + 1))} disabled={playbackStep >= totalSteps - 1}>
                  <StepForwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {playbackStep >= 0 && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#e65100', minWidth: 50 }}>
                  {playbackStep + 1}/{totalSteps}
                </Typography>
              )}
              {playbackStep >= 0 && (
                <Button size="small" variant="text" onClick={resetPlayback} sx={{ fontSize: '0.6rem', minWidth: 0, px: 0.5 }}>
                  Reset
                </Button>
              )}
              <Box sx={{ width: 80, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>Speed</Typography>
                <Slider
                  size="small"
                  min={0.25}
                  max={4}
                  step={0.25}
                  value={playbackSpeed}
                  onChange={(_, v) => setPlaybackSpeed(v)}
                  sx={{ py: 0 }}
                />
              </Box>
            </>
          )}

          {/* Debug panel toggle */}
          <Tooltip title="Toggle debug inspector">
            <IconButton
              size="small"
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              sx={{ ml: 'auto', color: showDebugPanel ? '#e65100' : 'text.secondary' }}
            >
              <PanelIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <Button size="small" variant="text" sx={{ fontSize: '0.6rem' }} onClick={loadExecutions}>
            Refresh
          </Button>
        </Box>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left: Block palette + Variables */}
        <Paper
          elevation={1}
          sx={{
            borderRadius: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BlockPalette />

          {/* Flow Variables Panel */}
          {showVariables && (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <VariablesIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'secondary.main', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Flow Variables
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.6rem' }}>
                Default values set at flow start. Trigger data and extract_params can override these.
                Reference in any config field: <code style={{ fontSize: '0.6rem' }}>{'{{varName}}'}</code>
              </Typography>

              {flowVariables.map((v, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="name"
                    value={v.name || ''}
                    onChange={(e) => {
                      const updated = [...flowVariables];
                      updated[index] = { ...updated[index], name: e.target.value.replace(/[^a-zA-Z0-9_.]/g, '') };
                      setFlowVariables(updated);
                    }}
                    sx={{ flex: 1 }}
                    inputProps={{ style: { fontSize: '0.7rem', padding: '4px 6px' } }}
                  />
                  <TextField
                    size="small"
                    placeholder="value"
                    value={v.value ?? ''}
                    onChange={(e) => {
                      const updated = [...flowVariables];
                      updated[index] = { ...updated[index], value: e.target.value };
                      setFlowVariables(updated);
                    }}
                    sx={{ flex: 1 }}
                    inputProps={{ style: { fontSize: '0.7rem', padding: '4px 6px' } }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setFlowVariables(flowVariables.filter((_, i) => i !== index))}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => setFlowVariables([...flowVariables, { name: '', value: '' }])}
                sx={{ fontSize: '0.65rem', mt: 0.5 }}
              >
                Add Variable
              </Button>

              <Divider sx={{ mt: 1, mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                <strong>Built-in variables:</strong><br />
                <code style={{ fontSize: '0.55rem' }}>{'{{locationId}}'}</code> — automation site<br />
                <code style={{ fontSize: '0.55rem' }}>{'{{query.paramName}}'}</code> — webhook query param<br />
                <code style={{ fontSize: '0.55rem' }}>{'{{body.fieldName}}'}</code> — webhook body field<br />
                <code style={{ fontSize: '0.55rem' }}>{'{{path.0}}'}</code> — webhook extra path segment<br />
                <code style={{ fontSize: '0.55rem' }}>{'{{trigger.endpointPath}}'}</code> — webhook endpoint
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Center: ReactFlow canvas */}
        <Box ref={reactFlowWrapper} sx={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode="Delete"
            attributionPosition="bottom-left"
          >
            <Controls position="bottom-right" />
            <Background variant="dots" gap={15} size={1} color="#e0e0e0" />
            <MiniMap
              nodeColor={(node) => categoryColors[node.type] || '#999'}
              maskColor="rgba(50,50,50,0.7)"
              position="top-right"
              style={{ width: 150, height: 100 }}
            />
            {nodes.length === 0 && (
              <Panel position="top-center">
                <Paper sx={{ px: 3, py: 2, mt: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
                  <Typography variant="body2" color="text.secondary">
                    Drag a <strong>Trigger</strong> block from the palette to start building your flow
                  </Typography>
                </Paper>
              </Panel>
            )}
          </ReactFlow>
        </Box>

        {/* Debug popover when clicking an edge */}
        <Popover
          open={!!debugPopover.anchorEl}
          anchorEl={debugPopover.anchorEl}
          onClose={() => setDebugPopover({ anchorEl: null, data: null })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {debugPopover.data && (
            <Paper sx={{ p: 2, maxWidth: 480, maxHeight: 400, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DebugIcon sx={{ fontSize: 16, color: '#e65100' }} />
                Data passing through edge
              </Typography>
              <Divider sx={{ mb: 1 }} />

              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Source: {debugPopover.data.sourceStep.label || debugPopover.data.sourceStep.blockType}
                <Chip
                  label={debugPopover.data.sourceStep.status}
                  size="small"
                  color={debugPopover.data.sourceStep.status === 'completed' ? 'success' : 'error'}
                  sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                />
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, mb: 1.5, bgcolor: '#f5f5f5' }}>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ fontFamily: 'monospace', fontSize: '0.65rem', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                  {debugPopover.data.sourceStep.output
                    ? JSON.stringify(debugPopover.data.sourceStep.output, null, 2)
                    : '(no output)'}
                </Typography>
              </Paper>
              {debugPopover.data.sourceStep.error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  Error: {debugPopover.data.sourceStep.error}
                </Typography>
              )}

              {debugPopover.data.targetStep && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Target: {debugPopover.data.targetStep.label || debugPopover.data.targetStep.blockType}
                    <Chip
                      label={debugPopover.data.targetStep.status}
                      size="small"
                      color={debugPopover.data.targetStep.status === 'completed' ? 'success' : 'error'}
                      sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                    />
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5' }}>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{ fontFamily: 'monospace', fontSize: '0.65rem', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {debugPopover.data.targetStep.output
                        ? JSON.stringify(debugPopover.data.targetStep.output, null, 2)
                        : '(no output)'}
                    </Typography>
                  </Paper>
                </>
              )}
            </Paper>
          )}
        </Popover>

        {/* Right panel: Debug Panel OR Node Config/Data Drawer */}
        {debugMode && showDebugPanel && !selectedNode && (
          <Paper
            elevation={1}
            sx={{
              width: 300,
              borderRadius: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DebugPanel
              execution={selectedExecution}
              executionMap={executionMap}
              selectedNodeId={selectedNodeId}
              onStepClick={(step) => setSelectedNodeId(step.nodeId)}
            />
          </Paper>
        )}

        {/* Node config / data drawer */}
        {selectedNode && (
          <Paper
            elevation={1}
            sx={{
              width: 340,
              borderRadius: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {debugMode && executionMap[selectedNode.id] ? (
              <NodeDataDrawer
                node={selectedNode}
                executionStep={executionMap[selectedNode.id]}
                locationId={locationId}
                debugMode={debugMode}
                onConfigChange={(newData) => handleNodeConfigChange(selectedNode.id, newData)}
                onDelete={handleDeleteNode}
                onClose={() => setSelectedNodeId(null)}
              />
            ) : (
              <>
                {/* Standard config panel */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: categoryColors[selectedNode.type] || '#666',
                    color: 'white',
                  }}
                >
                  <Typography variant="subtitle2">
                    Configure: {selectedNode.data.label}
                  </Typography>
                  <Box>
                    <IconButton size="small" sx={{ color: 'white' }} onClick={handleDeleteNode}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: 'white' }} onClick={() => setSelectedNodeId(null)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Divider />
                <Box sx={{ p: 2, flex: 1 }}>
                  <NodeConfigPanel
                    node={selectedNode}
                    locationId={locationId}
                    onChange={(newData) => handleNodeConfigChange(selectedNode.id, newData)}
                  />
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AutomationEditor;
